/**
 * Putting a region's terrain surface into the scene — the one step that turns
 * `packages/engines flight/terrain-relief.ts`'s field into pixels.
 *
 * A sibling of `flight-scene.ts` rather than part of it, for the reason
 * `scene-draw.ts`, `scene-propellers.ts` and `scene-killcam.ts` are: that file
 * is at the size gate and new scene logic goes beside it.
 *
 * Two things happen, and the second is easy to forget. The relief mesh is added
 * to the region's own group, so it is disposed with everything else the region
 * owns. And the flat backdrop plane is **sunk below the relief's floor** — left
 * at the datum it would cut through every valley, since it is a 300 km quad and
 * the valleys are 50 m deep. Past the relief mesh's rim the backdrop is still
 * the only thing drawn, which is why it is lowered rather than removed.
 */

import type * as THREE from "three/webgpu";
import type { ReliefGround } from "../three/relief-ground.ts";
import { buildTerrainDecoration, type TerrainDecoration } from "../three/terrain-decoration.ts";
import { reliefIsFlat, type ReliefField } from "@red-opus/engine";
import { SEA_PLATE_DEPTH_M } from "../world/water-mask.ts";
import { PLATE_LAYER_LIFT_M } from "../three/plate-paint-order.ts";
import type { TerrainFeatures } from "../world/terrain-features.ts";
import type { SkyDressing } from "./sky-dressing.ts";

/**
 * Extra metres the backdrop plane is dropped below the deepest point the relief
 * can reach. Enough that no valley floor ever meets it, small enough that the
 * step at the mesh rim — 80 km away, past the far plane — is never in frame.
 */
export const BACKDROP_CLEARANCE_M = 20;

/**
 * Where the backdrop plane goes on a region with **no relief** — and the fix for
 * a defect that had been on screen, unreported, since the sea was given its
 * draught.
 *
 * ## The sea had never once been drawn in `/fly`
 *
 * `world/water-mask.ts` sinks the two `s = 10` sea plates a metre below the
 * datum so that water is unambiguously *under* land. `world.ts`'s backdrop is a
 * **300 km opaque quad at −0.08 m**. A coastal map has no relief, so nothing
 * ever called `setBackdropFloor` on it, so the backdrop sat 0.92 m *above* the
 * sea across the whole of the drawn world — and the far plane is 26 km. London
 * and Dunkirk have therefore been flown as unbroken green plains with the
 * Channel painted out of them.
 *
 * Nothing caught it, and the reason is worth writing down: every check the sea
 * has is a **predicate** check. `london-sea-placements.test.ts` and
 * `terr1-00-original-raster.test.ts` both score `isWater` against what the
 * original paints and both pass at 349 of 361 sampled points — but they ask a
 * CPU function, not a framebuffer, and `world-instancer.ts`'s draw-call census
 * counts the plate's mesh whether or not a single one of its pixels survives the
 * depth test. It is `lessons.md`'s oldest rule in this repo — *a draw call is
 * not evidence that anything was drawn* — with the object under test being the
 * biggest single polygon in the game.
 *
 * ## Why this is a fidelity fix and not a new picture
 *
 * The original has no depth buffer: it paints the default ground first and then
 * `TERR1-00`, which is placement **#0**, over it. The sea is *supposed* to be
 * there, and the raster test above is the measurement of exactly where. So the
 * authentic path — ENHANCED TERRAIN off — gains the flat `BARON.PAL` 4 sea it
 * always should have had, and the toggle then chooses how that sea is painted.
 *
 * ## The number
 *
 * `SEA_PLATE_DEPTH_M` plus one `PLATE_LAYER_LIFT_M`, i.e. −1.004 m: the smallest
 * step that clears the sea, using the port's own one-layer constant rather than
 * a new one. `BACKDROP_CLEARANCE_M`'s 20 m was considered and rejected — the
 * backdrop is what shows *between* the land plates, so its offset is also the
 * height of the step at every plate edge, and 21 m of that is a visible ledge at
 * low level where 1 m is the draught the sea already carries invisibly.
 */
export const FLAT_BACKDROP_FLOOR_M = -(SEA_PLATE_DEPTH_M + PLATE_LAYER_LIFT_M);

export interface RegionRelief {
  readonly relief: ReliefField;
  readonly surface: ReliefGround | undefined;
  readonly features: TerrainFeatures;
  readonly instances: {
    readonly group: THREE.Group;
    /** ENHANCED TERRAIN's field hillshading, swapped with the toggle (`three/world-instancer.ts`). */
    readonly setLandcoverShading: (enabled: boolean) => void;
    /** The original's flat canopy discs, hidden while the enhanced foliage stands in for them. */
    readonly setCanopyDiscsVisible: (visible: boolean) => void;
    /** ENHANCED MODELS for the region's buildings, swapped with the toggle (`three/world-instancer.ts`). */
    readonly enhancedStructures: { readonly set: (enabled: boolean) => void };
    /** ENHANCED TERRAIN's sea, swapped with the same toggle (`three/ocean-plates.ts`). */
    readonly ocean: {
      readonly plateCount: number;
      readonly set: (enabled: boolean) => void;
      readonly setFrame: (timeS: number, anchorX: number, anchorZ: number) => void;
    };
    readonly shadedPlateCount: number;
  };
}

export interface TerrainSurface {
  /** Triangles the surface added, for the perf readout. 0 when the region is flat. */
  readonly triangleCount: number;
  /** Draw calls it added: 1, or 0 for a flat region. */
  readonly drawCallCount: number;
  /**
   * ENHANCED TERRAIN, both halves: the trees (built once, on the first request
   * that asks for them) and the **field hillshading**, which is the half a
   * pilot at altitude can actually see — the trees are 14 m and about 2 px at
   * 6 000 ft, whereas the shading repaints every field in frame.
   */
  readonly setDecoration: (enabled: boolean) => void;
  /**
   * ENHANCED MODELS, the ground half: swaps the region's buildings between the
   * bake's flat cards and their enhanced `.glb`.
   *
   * Separate from `setDecoration` because it answers a different switch. The
   * two are easy to confuse and were confused in the code that came before
   * this: ENHANCED TERRAIN is the landscape — hillshading and foliage — and
   * ENHANCED MODELS is the objects standing on it, which until now meant
   * aeroplanes and three raid targets and nothing a town places.
   */
  readonly setEnhancedStructures: (enabled: boolean) => void;
  /**
   * One rendered frame's sea, on the **encounter's** clock and the player's own
   * pose (`three/ocean-material.ts`'s `setFrame`).
   *
   * A no-op on the three inland maps and while ENHANCED TERRAIN is off. It is
   * called from `scene-draw.ts` beside the ships and the searchlights, which is
   * where every other piece of world geometry that moves is advanced exactly
   * once a frame — and, like them, it must not be advanced again in the kill
   * camera's re-draw or the two passes would photograph two different seas.
   */
  readonly setOceanFrame: (timeS: number, anchorX: number, anchorZ: number) => void;
  /** How many sea plates the toggle has to work with — 1 on London and Dunkirk, 0 elsewhere. Reported for `?boot-report` and the tests, the way `shadedPlateCount` is. */
  readonly oceanPlateCount: number;
  /** What the decoration costs, once it has been built; `undefined` until then. */
  readonly decoration: () => TerrainDecoration | undefined;
  /**
   * Walks the whole terrain down its own ramp for the hour.
   *
   * Live, because ENHANCED SKY is a toggle and the briefed sky word darkens the
   * ground under both treatments: before this, the sky and the
   * flat ground plane dimmed together and the *relief* stayed at noon, so a
   * night mission drew noon-bright fields under a black sky — and the
   * searchlight beams (AD-017) read as grey haze against them instead of light.
   * The ground half is a colour rewrite; the trees are rebuilt, which is why
   * the shift is remembered and re-applied if the decoration is switched on
   * later.
   */
  readonly setHourShift: (shift: number) => void;
}

/**
 * Builds and attaches the region's ground. A region with no relief
 * (`FLAT_RELIEF` — the two coastal maps, the practice range) draws nothing new
 * and keeps the flat backdrop it has always had, so this is a no-op there
 * rather than a special case downstream.
 */
export function applyRegionRelief(dressing: SkyDressing, region: RegionRelief): TerrainSurface {
  const { options } = region.relief;
  const reach = options.amplitudeM + options.riverDepthM;
  if (region.surface && !reliefIsFlat(region.relief)) {
    region.instances.group.add(region.surface.mesh);
    dressing.setBackdropFloor(-(reach + BACKDROP_CLEARANCE_M));
    return { ...decorationControl(region), triangleCount: region.surface.triangleCount, drawCallCount: 1 };
  }
  // A flat region (the two coastal maps, the practice range) draws no relief
  // mesh — but it still has something under the datum, and until this line the
  // backdrop was painted over it. See `FLAT_BACKDROP_FLOOR_M`.
  dressing.setBackdropFloor(FLAT_BACKDROP_FLOOR_M);
  return { ...decorationControl(region), triangleCount: 0, drawCallCount: 0 };
}

/**
 * Lazily builds the decoration the first time it is switched on, then keeps it
 * and only adds/removes the group. Rebuilding on every flip would pay
 * `dispose-tree.ts`'s pipeline-cache cost twice a click, which is the same
 * argument `sky-dressing.ts` makes for guarding its own re-dress.
 */
function decorationControl(
  region: RegionRelief,
): Pick<
  TerrainSurface,
  "setDecoration" | "setEnhancedStructures" | "decoration" | "setHourShift" | "setOceanFrame" | "oceanPlateCount"
> {
  let built: TerrainDecoration | undefined;
  let attached = false;
  let hourShift = 0;

  const build = (): TerrainDecoration =>
    buildTerrainDecoration(
      region.features.copseSites,
      // The same list the instancer painted the ground with — see
      // `world/terrain-features.ts` for why it is derived once.
      region.features.woodSites,
      region.surface?.drapeAt ?? (() => 0),
      hourShift,
    );

  return {
    decoration: () => built,
    setEnhancedStructures: (enabled: boolean) => region.instances.enhancedStructures.set(enabled),
    oceanPlateCount: region.instances.ocean.plateCount,
    setOceanFrame: (timeS, anchorX, anchorZ) => region.instances.ocean.setFrame(timeS, anchorX, anchorZ),
    setDecoration: (enabled: boolean) => {
      // The sea, on the same switch as the hillshading and for the reason G75
      // recorded: on the two coastal maps neither of the other two halves has
      // anything to do, because both gate behind relief and a coastal map has
      // none. This is what ENHANCED TERRAIN *means* on London and Dunkirk.
      // Cheap and idempotent, like the shading below it.
      region.instances.ocean.set(enabled);
      // Cheap and idempotent, so it is not guarded by `attached` — and it must
      // not be, because the shading is a buffer swap rather than a scene-graph
      // add and has no "already there" state of its own.
      region.instances.setLandcoverShading(enabled);
      // The heart of the enhancement: the original's canopies are flat filled
      // discs (AD-019 §2.2), and the decoration puts a solid tree on each of
      // the same trunks. Both at once is each tree drawn twice, so the discs go
      // away exactly while the foliage is up — and come straight back when it
      // is not, which is what keeps the authentic path pixel-identical.
      region.instances.setCanopyDiscsVisible(!enabled);
      if (enabled === attached) return;
      if (enabled) {
        built ??= build();
        region.instances.group.add(built.group);
      } else if (built) {
        region.instances.group.remove(built.group);
      }
      attached = enabled;
    },
    setHourShift: (shift: number) => {
      if (shift === hourShift) return;
      hourShift = shift;
      region.surface?.setRampShift(shift);
      // A tree's colours are baked into its four shared geometries, so the hour
      // means new ones. Only paid when the decoration is actually up, and only
      // on a toggle flip — a mission's hour does not change mid-flight.
      if (!built) return;
      const wasAttached = attached;
      if (wasAttached) region.instances.group.remove(built.group);
      built.dispose();
      built = build();
      if (wasAttached) region.instances.group.add(built.group);
    },
  };
}
